// The signed-in visitor, as the lead form uses them: their profile
// supplies the contact step's values, so they submit as themselves
// without retyping, and an input is asked only for a value the profile
// lacks.

import type { Answers } from "./answers";
import { isValidEmail, isValidName, nationalPhoneDigits } from "./validation";

/** Each field is empty when the profile has no usable value for it. */
export interface Account {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

interface Profile {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
}

function formatNationalPhone(value: string | null | undefined): string {
  const digits = nationalPhoneDigits(value ?? "");
  if (digits === null) return "";
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/** The account for a signed-in visitor; `profile` is null while their
 * profile is not available, which leaves every field to be typed. */
export function accountFrom(profile: Profile | null): Account {
  const first = (profile?.first_name ?? "").trim();
  const last = (profile?.last_name ?? "").trim();
  const email = (profile?.email ?? "").trim();
  const named = isValidName(first) && isValidName(last);
  return {
    firstName: named ? first : "",
    lastName: named ? last : "",
    email: isValidEmail(email) ? email : "",
    phone: formatNationalPhone(profile?.phone),
  };
}

/** How the signed-in visitor is named back to them: their email, or their
 * phone as people write it when there is no email. Empty when the profile
 * has neither. */
export function signedInIdentity(account: Account): string {
  return account.email || account.phone;
}

export function accountHasName(account: Account): boolean {
  return account.firstName !== "" && account.lastName !== "";
}

/** The answers with the account's values written into the submitter.
 * Values the account lacks are left as typed. Returns `answers` itself
 * when nothing changes. */
export function withAccountContact(
  answers: Answers,
  account: Account,
): Answers {
  const { submitter } = answers;
  const next = {
    firstName: accountHasName(account)
      ? account.firstName
      : submitter.firstName,
    lastName: accountHasName(account) ? account.lastName : submitter.lastName,
    email: account.email || submitter.email,
    phone: account.phone || submitter.phone,
  };
  const unchanged =
    next.firstName === submitter.firstName &&
    next.lastName === submitter.lastName &&
    next.email === submitter.email &&
    next.phone === submitter.phone;
  return unchanged ? answers : { ...answers, submitter: next };
}
